import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { CoveButton } from "@/components/CoveButton";
import { ArchImage } from "@/components/ArchImage";
import { Reveal, Pop, FloatingBlob } from "@/components/motion";
import { SERVICES, PACKAGES, SITE, IMAGES } from "@/data/content";
import { Check } from "lucide-react";

const offerSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Craniosacral Therapy",
  provider: { "@type": "Organization", name: "Cranio Cove" },
  areaServed: SITE.serviceArea,
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Craniosacral Sessions",
    itemListElement: SERVICES.map((s) => ({
      "@type": "Offer",
      name: s.name,
      description: s.description,
    })),
  },
};

export default function Services() {
  return (
    <>
      <Seo
        title="Services & Pricing"
        description="Craniosacral session options at Cranio Cove, initial, follow-up and extended sessions. Clear durations and a simple cancellation policy. Book the session that suits you."
        path="/services"
        schema={offerSchema}
      />
      <PageHero
        overline="Services & pricing"
        titleLines={["Sessions,", "gently paced", "to suit you"]}
        intro="Each session is private and one-to-one. Choose the option that feels right, and if you're unsure, Laura is glad to help you decide."
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])}
        heroImage={{ src: IMAGES.coveMagenta, alt: "A vivid magenta sunset over a calm lake with a wooden dock" }}
      />

      <section className="relative px-6 md:px-12 py-12 md:py-20 overflow-hidden" data-testid="services-grid">
        <FloatingBlob color="sage" size={360} className="top-20 -left-24" delay={0.1} />
        <FloatingBlob color="blush" size={320} className="bottom-10 -right-20" delay={0.4} drift={30} />
        <div className="relative mx-auto max-w-[1400px] grid md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const tints = ["bg-cove-blush/30", "bg-cove-sage/25", "bg-cove-blue/25"];
            const accents = ["bg-cove-rose", "bg-cove-sage", "bg-cove-blue"];
            return (
            <Reveal key={s.id} delay={i * 0.1}>
              <Pop
                className={`relative h-full flex flex-col overflow-hidden border border-cove-slate/15 ${tints[i % 3]} px-8 md:px-10 pt-24 md:pt-28 pb-8 md:pb-10`}
                style={{ borderTopLeftRadius: "50% 22%", borderTopRightRadius: "50% 22%", borderBottomLeftRadius: "2rem", borderBottomRightRadius: "2rem" }}
                testId={`service-card-${s.id}`}
              >
                <p className="text-xs tracking-[0.2em] uppercase text-cove-taupe">{s.duration}</p>
                <h2 className="mt-4 font-serif text-3xl text-cove-slate leading-tight">{s.name}</h2>
                <p className="mt-5 text-cove-olive leading-relaxed">{s.description}</p>

                <ul className="mt-7 space-y-3">
                  {s.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-cove-olive">
                      <Check size={16} className="mt-0.5 text-cove-slate shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-cove-slate/15 flex items-end justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-cove-taupe">From</p>
                    <p className="font-serif text-4xl text-cove-slate" data-testid={`service-price-${s.id}`}>{s.price}</p>
                  </div>
                </div>

                <div className="mt-6">
                  <CoveButton
                    calendlyUrl={s.calendlyUrl}
                    variant="primary"
                    className="w-full"
                    testId={`service-book-${s.id}`}
                  >
                    Book This Session
                  </CoveButton>
                </div>
              </Pop>
            </Reveal>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 md:py-24" data-testid="services-policy">
        <div className="mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-10 lg:gap-24">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">Packages</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate">Session packages</h2>
            <div className="mt-8 space-y-4">
              {PACKAGES.map((pkg) => (
                <div
                  key={pkg.id}
                  className="rounded-[1.5rem] bg-cove-blush/30 border border-cove-slate/10 p-6 md:p-7"
                  data-testid={`package-${pkg.id}`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif text-2xl text-cove-slate">{pkg.name}</h3>
                    <p className="font-serif text-3xl text-cove-slate">{pkg.price}</p>
                  </div>
                  <p className="mt-1 text-xs tracking-[0.2em] uppercase text-cove-taupe">
                    {pkg.priceNote}
                  </p>
                  <p className="mt-4 text-cove-olive leading-relaxed">{pkg.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">Availability</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate">
              When & where
            </h2>
            <ul className="mt-6 space-y-3 text-cove-olive">
              {SITE.hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-4 border-b border-cove-slate/10 pb-3">
                  <span>{h.day}</span>
                  <span className="text-cove-slate">{h.time}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-cove-olive leading-relaxed text-center">
              Cranio Cove is located within{" "}
              <a
                href={SITE.hearthUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-cove-rose/40 underline-offset-2 hover:text-cove-slate transition-colors"
                data-testid="services-hearth-link"
              >
                {SITE.hearthName}
              </a>
              {" "}, 200 S. Broadway, Building 1, Suite 100, Tarrytown, NY 10591.
              <br />
              <a
                href={SITE.hearthUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline decoration-cove-rose/40 underline-offset-2 hover:text-cove-slate transition-colors"
                data-testid="services-hearth-url"
              >
                www.wellwombn.com
              </a>
            </p>
            <p className="mt-6 text-cove-olive leading-relaxed">
              <span className="text-xs tracking-[0.25em] uppercase text-cove-rose block mb-2">Cancellation policy</span>
              Please provide at least 24 hours' notice to reschedule or cancel a session. Life
              happens, please just let Laura know as early as you can.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-4" data-testid="services-payments">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="rounded-[2rem] bg-cove-sage/20 border border-cove-slate/10 px-8 md:px-12 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-2">Payment</p>
                <h2 className="font-serif text-3xl md:text-4xl text-cove-slate">Simple, flexible payment</h2>
                <p className="mt-2 text-cove-olive">Payment is due at the time of your session.</p>
              </div>
              <ul className="flex flex-wrap gap-3">
                {SITE.payments.map((p) => (
                  <li key={p} className="rounded-full bg-white/70 border border-cove-slate/15 px-6 py-3 text-sm font-medium text-cove-slate" data-testid={`payment-${p.toLowerCase()}`}>{p}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-28 md:pb-40 pt-16" data-testid="services-cta">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="rounded-[2rem] md:rounded-[3rem] bg-cove-slate text-cove-cream px-8 md:px-16 py-16 md:py-24 text-center grain">
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.95]">Reserve your time.</h2>
              <p className="mt-6 max-w-xl mx-auto text-cove-blue leading-relaxed">
                Send a request and Laura will personally confirm a time that works for you.
              </p>
              <div className="mt-10">
                <CoveButton calendly variant="light" testId="services-cta-button">Book a Session</CoveButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
